import { useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { useTheme } from '../../context/ThemeContext';

// Shared labelled form field (DESIGN_LANGUAGE_SPEC §27 forms). Wires a real <label> to its
// control via a generated id, associates any error/hint text via aria-describedby, and sets
// aria-invalid on error — so every form field meets the chunk's a11y bar (labelled fields with
// error association) without each call site re-implementing it.

interface FieldShellProps {
  label: string;
  htmlFor: string;
  hint?: ReactNode | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
  describedById: string;
  children: ReactNode;
}

function FieldShell({
  label,
  htmlFor,
  hint,
  error,
  required,
  describedById,
  children,
}: FieldShellProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={htmlFor}
        className={`block text-xs font-medium ${isDark ? 'text-white/60' : 'text-slate-600'}`}
      >
        {label}
        {required && (
          <span className={isDark ? 'text-rose-400' : 'text-rose-500'} aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </label>
      {children}
      {error ? (
        <p id={describedById} className={`text-xs ${isDark ? 'text-rose-300' : 'text-rose-600'}`}>
          {error}
        </p>
      ) : hint ? (
        <p id={describedById} className={`text-xs ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  hint?: ReactNode | undefined;
  error?: string | undefined;
}

export function TextField({ label, hint, error, required, className, ...rest }: TextFieldProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const id = useId();
  const describedById = `${id}-desc`;
  const hasDesc = Boolean(error || hint);

  return (
    <FieldShell
      label={label}
      htmlFor={id}
      hint={hint}
      error={error}
      required={required}
      describedById={describedById}
    >
      <input
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={hasDesc ? describedById : undefined}
        className={`min-h-11 w-full rounded-xl border px-4 py-2.5 text-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30 ${
          error
            ? isDark
              ? 'border-rose-500/40 bg-rose-500/[0.04]'
              : 'border-rose-300 bg-rose-50/40'
            : isDark
              ? 'border-white/[0.08] bg-white/[0.04] text-white placeholder-white/30 focus:border-indigo-500'
              : 'border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:border-indigo-500'
        } ${className ?? ''}`}
        {...rest}
      />
    </FieldShell>
  );
}

export { FieldShell };
